import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { FirestoreTeam } from '../types/auth';
import { getUserTeams } from '../utils/firestore';
import { useAppStore } from '../state/appStore';

interface TeamContextType {
  currentTeam: FirestoreTeam | null;
  teams: FirestoreTeam[];
  loading: boolean;
  initialLoadComplete: boolean;
  setCurrentTeam: (teamId: string) => void;
  refreshTeams: () => Promise<void>;
}

const TeamContext = React.createContext<TeamContextType>({} as TeamContextType);

export const useTeam = () => {
  return useContext(TeamContext);
};

export const TeamProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [teams, setTeams] = useState<FirestoreTeam[]>([]);
  const [currentTeamId, setCurrentTeamId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const setCurrentTeamIdInAppStore = useAppStore((state) => state.setCurrentTeamId);
  const loadFromFirestore = useAppStore((state) => state.loadFromFirestore);

  // useEffectの依存配列安定化のためrefで保持
  const setCurrentTeamIdRef = useRef(setCurrentTeamIdInAppStore);
  setCurrentTeamIdRef.current = setCurrentTeamIdInAppStore;
  const loadFromFirestoreRef = useRef(loadFromFirestore);
  loadFromFirestoreRef.current = loadFromFirestore;

  const refreshTeams = async () => {
    if (!currentUser) {
      setTeams([]);
      setCurrentTeamId(null);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const userTeams = await getUserTeams(currentUser.uid);
      setTeams(userTeams);
      
      // 現在のチームがまだ存在するか確認
      if (currentTeamId && !userTeams.some(t => t.id === currentTeamId)) {
        // 現在のチームが削除された場合、最初のチームに切り替え
        if (userTeams.length > 0) {
          const firstId = userTeams[0].id;
          setCurrentTeamId(firstId);
          localStorage.setItem('currentTeamId', firstId);
          await setCurrentTeamIdInAppStore(firstId);
        } else {
          setCurrentTeamId(null);
          setCurrentTeamIdInAppStore(null);
        }
      } else if (currentTeamId) {
        // チームが存在する場合、AppStoreのデータを再読み込み
        await loadFromFirestore();
      } else if (!currentTeamId && userTeams.length > 0) {
        // チーム未選択だが参加済み（初回参加直後）→ 最初のチームを選択
        const firstId = userTeams[0].id;
        setCurrentTeamId(firstId);
        localStorage.setItem('currentTeamId', firstId);
        await setCurrentTeamIdInAppStore(firstId);
      }
    } catch (error) {
      console.error('Failed to refresh teams:', error);
      setTeams([]);
      setCurrentTeamId(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser) {
      setTeams([]);
      setCurrentTeamId(null);
      setLoading(false);
      setInitialLoadComplete(true);
      // AppStoreにもリセットを通知
      setCurrentTeamIdRef.current(null);
      return;
    }

    let cancelled = false;

    const loadUserTeams = async () => {
      try {
        setLoading(true);
        console.log('[TeamContext] Loading teams for user:', currentUser.uid);
        const userTeams = await getUserTeams(currentUser.uid);
        console.log('[TeamContext] Loaded teams:', userTeams.length, userTeams.map(t => t.id));

        if (cancelled) return;

        setTeams(userTeams);

        const savedTeamId = localStorage.getItem('currentTeamId');
        let selectedTeamId: string | null = null;

        if (savedTeamId && userTeams.some(t => t.id === savedTeamId)) {
          selectedTeamId = savedTeamId;
          setCurrentTeamId(savedTeamId);
        } else if (userTeams.length > 0) {
          selectedTeamId = userTeams[0].id;
          setCurrentTeamId(userTeams[0].id);
        }

        // AppStoreにチームIDを設定（Firestoreからデータを読み込む）
        if (selectedTeamId) {
          await setCurrentTeamIdRef.current(selectedTeamId);
        }
      } catch (error) {
        console.error('[TeamContext] Failed to load teams:', error);
        if (!cancelled) {
          setTeams([]);
          setCurrentTeamId(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setInitialLoadComplete(true);
        }
      }
    };

    loadUserTeams();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const setCurrentTeam = (teamId: string) => {
    if (teams.some(t => t.id === teamId)) {
      setCurrentTeamId(teamId);
      localStorage.setItem('currentTeamId', teamId);
      
      // AppStoreにも通知（Firestoreからデータを読み込む）
      setCurrentTeamIdInAppStore(teamId);
    }
  };

  const currentTeam = teams.find(t => t.id === currentTeamId) || null;

  const value = {
    currentTeam,
    teams,
    loading,
    initialLoadComplete,
    setCurrentTeam,
    refreshTeams,
  };

  return (
    <TeamContext.Provider value={value}>
      {children}
    </TeamContext.Provider>
  );
};
